import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { SpinnerGap, ArrowLeft, Package, CreditCard, Truck, MapPin, Receipt, ChartBar } from '@phosphor-icons/react';

function HtmlContent({ html, className = '' }) {
  if (!html) return <span>-</span>;
  // Render as plain text preserving CR/LF breaks — no HTML execution (safe against XSS)
  const lines = String(html).split(/\r?\n/);
  return (
    <span className={className}>
      {lines.map((line, i) => (
        <span key={`ln-${i}-${line.slice(0, 8)}`}>{line}{i < lines.length - 1 && <br />}</span>
      ))}
    </span>
  );
}

const fmt = (v, currency = 'EUR') => new Intl.NumberFormat('pt-PT', { style: 'currency', currency }).format(v || 0);
const fmtPct = (v) => new Intl.NumberFormat('pt-PT', { style: 'percent', minimumFractionDigits: 2 }).format(v || 0);
const fmtDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('pt-PT'); } catch { return d; }
};

function InfoLabel({ children }) {
  return (
    <p data-testid="info-label" className="text-xs font-semibold uppercase tracking-[0.15em] text-[#6B7280] bg-[#E0F3FF] text-center py-1.5 px-2 rounded mb-1">
      {children}
    </p>
  );
}

function InfoValue({ children, className = '' }) {
  return <p className={`text-sm text-center text-[#111827] ${className}`}>{children}</p>;
}

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInfo, setShowInfo] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileMsg, setReconcileMsg] = useState(null);

  const loadOrder = () => {
    setLoading(true);
    api.post(`/encomendas/${id}/fullDetail`)
      .then(r => {
        if (r.data?.success) setData(r.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    api.get(`/encomendas/${id}/billing`)
      .then(r => { if (r.data?.success) setBilling(r.data); })
      .catch(() => {});
  };

  useEffect(() => { loadOrder(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const handleReconcile = async () => {
    setReconciling(true);
    setReconcileMsg(null);
    try {
      const { data: res } = await api.post(`/encomendas/${id}/reconcile-payment`);
      if (res?.found) {
        setReconcileMsg({
          type: res.success ? 'success' : 'warning',
          text: res.message || (res.success
            ? `Pagamento ${res.payment_status} reconciliado.`
            : `Pagamento ${res.payment_status} encontrado mas não foi possível avançar para PR automaticamente.`),
        });
        if (res.success) loadOrder();
      } else {
        setReconcileMsg({
          type: 'info',
          text: res?.message || 'Nenhum pagamento pendente ou pago encontrado na EasyPay.',
        });
      }
    } catch (e) {
      setReconcileMsg({
        type: 'error',
        text: e?.response?.data?.detail || 'Erro a comunicar com EasyPay.',
      });
    } finally {
      setReconciling(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0F1829]" /></div>;

  if (!data || !data.Detalhes) {
    return (
      <div data-testid="order-detail-empty" style={{ fontFamily: "'Manrope', sans-serif" }}>
        <button data-testid="back-to-orders-btn" onClick={() => navigate('/encomendas')} className="flex items-center gap-1.5 text-sm text-[#0F1829] hover:underline mb-4">
          <ArrowLeft size={16} /> Voltar
        </button>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center">
          <p className="text-sm text-[#6B7280]">Não existem informações sobre esta encomenda</p>
        </div>
      </div>
    );
  }

  const d = data.Detalhes;
  const items = data.Itens || [];
  const states = data.Estados || [];
  const cur = d.ID_Moeda || 'EUR';

  return (
    <div data-testid="order-detail-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button data-testid="back-to-orders-btn" onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-[#0F1829] hover:underline">
            <ArrowLeft size={16} /> Voltar
          </button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>
              Detalhe da Encomenda
            </h1>
            <p className="text-sm text-[#6B7280] mt-0.5">Encomenda #{id}</p>
          </div>
        </div>
      </div>

      {/* SECÇÃO 1: Informações da Encomenda */}
      <div data-testid="order-info-section" className="bg-white border border-[#E5E7EB] rounded-lg mb-4 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
          <div className="flex items-center gap-2">
            <Receipt size={18} className="text-[#6B7280]" />
            <h2 className="text-sm font-semibold text-[#111827]">Informações da encomenda</h2>
          </div>
          <button
            data-testid="toggle-info-btn"
            onClick={() => setShowInfo(!showInfo)}
            className="text-xs font-medium text-[#0F1829] hover:underline"
          >
            {showInfo ? 'Ocultar' : 'Mostrar'}
          </button>
        </div>

        {showInfo && (
          <div className="p-5 space-y-5">
            {/* Multibanco info */}
            {d.ID_TipoPagamento === 'MB' && (
              <div data-testid="mb-payment-info" className="bg-[#F0F7FF] border border-[#BFDBFE] rounded-lg p-4">
                <InfoLabel>Informação de Pagamento</InfoLabel>
                <div className="flex flex-col sm:flex-row items-center gap-4 mt-2">
                  <img src="https://my.ihavethepower.net/Content/imagens/emails/multibanco.png" alt="Multibanco" className="h-12" />
                  <div className="text-sm space-y-0.5">
                    <p><span className="font-semibold">Entidade:</span> {d.EntidadeMB}</p>
                    <p><span className="font-semibold">Referência:</span> {d.ReferenciaMB}</p>
                    <p><span className="font-semibold">Valor:</span> {fmt(d.PrecoFinal, cur)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Row 1: ID + Data */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <InfoLabel>ID de Encomenda</InfoLabel>
                <InfoValue className="font-mono font-semibold">{d.ID_Encomenda}</InfoValue>
              </div>
              <div>
                <InfoLabel>Data da Encomenda</InfoLabel>
                <InfoValue>{fmtDate(d.DataEncomenda)}</InfoValue>
              </div>
            </div>

            {/* Row 1b: Cliente + Consultor */}
            {(d.NomeCliente || d.ID_Cliente || d.NomeConsultor) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(d.NomeCliente || d.ID_Cliente) && (
                  <div>
                    <InfoLabel>Cliente</InfoLabel>
                    <InfoValue data-testid="order-client-name">
                      <span className="font-semibold">{d.NomeCliente || '—'}</span>
                      {d.ID_Cliente ? <span className="ml-1 text-[#6B7280]">({d.ID_Cliente})</span> : null}
                    </InfoValue>
                    {(d.EmailCliente || d.TelemovelCliente) && (
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-[#6B7280]" data-testid="order-client-contacts">
                        {d.EmailCliente && (
                          <a href={`mailto:${d.EmailCliente}`} className="hover:text-[#0F1829] hover:underline" data-testid="order-client-email">
                            {d.EmailCliente}
                          </a>
                        )}
                        {d.TelemovelCliente && (
                          <a href={`tel:${String(d.TelemovelCliente).replace(/\s/g, '')}`} className="hover:text-[#0F1829] hover:underline" data-testid="order-client-phone">
                            {d.TelemovelCliente}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {(d.NomeConsultor || d.ID_Consultor) && (
                  <div>
                    <InfoLabel>Consultor</InfoLabel>
                    <InfoValue data-testid="order-consultor-name">
                      <span className="font-semibold">{d.NomeConsultor || '—'}</span>
                      {d.ID_Consultor ? <span className="ml-1 text-[#6B7280]">({d.ID_Consultor})</span> : null}
                    </InfoValue>
                  </div>
                )}
              </div>
            )}

            {/* Row 2: Estado + Transporte */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <InfoLabel>Estado da Encomenda</InfoLabel>
                <InfoValue>
                  <span className="inline-block text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 rounded border bg-blue-50 text-[#0F1829] border-[#0F1829]/20">
                    {d.EstadoEncomenda}
                  </span>
                </InfoValue>
              </div>
              <div>
                <InfoLabel>Tipo de Transporte</InfoLabel>
                <InfoValue>{d.DescricaoTipoTransporte || '-'}</InfoValue>
              </div>
            </div>

            {/* Row 3: Pagamento + Datas */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <InfoLabel>Tipo de Pagamento</InfoLabel>
                <InfoValue>{d.DescricaoTipoPagamento || '-'}</InfoValue>
                {d.ID_EstadoEncomenda === 'AP' && ['MB','MW','CT'].includes(d.ID_TipoPagamento) && (
                  <div className="mt-2 flex flex-col items-center gap-1.5">
                    <button
                      data-testid="reconcile-easypay-btn"
                      onClick={handleReconcile}
                      disabled={reconciling}
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded border border-[#0F1829] text-[#0F1829] hover:bg-[#0F1829] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Sincroniza com a EasyPay caso o webhook não tenha chegado"
                    >
                      {reconciling ? (
                        <><SpinnerGap size={12} className="animate-spin" /> A sincronizar…</>
                      ) : (
                        <>Reconciliar EasyPay</>
                      )}
                    </button>
                    {reconcileMsg && (
                      <p
                        data-testid="reconcile-feedback"
                        className={
                          'text-[11px] text-center max-w-[260px] ' + ({
                            success: 'text-emerald-700',
                            warning: 'text-amber-700',
                            info: 'text-[#6B7280]',
                            error: 'text-red-600',
                          }[reconcileMsg.type] || 'text-[#6B7280]')
                        }
                      >
                        {reconcileMsg.text}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div>
                <InfoLabel>Data de Pagamento</InfoLabel>
                <InfoValue>{fmtDate(d.DataPagamento)}</InfoValue>
              </div>
              <div>
                <InfoLabel>Data de Envio</InfoLabel>
                <InfoValue>{fmtDate(d.DataEnvio)}</InfoValue>
              </div>
            </div>

            {/* Row 4: Moradas */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <InfoLabel>Dados de Envio</InfoLabel>
                {d.moradaEnvioMorada ? (
                  <div className="text-sm text-center text-[#111827] space-y-0.5">
                    <p>{d.moradaEnvioNome}</p>
                    <p>{d.moradaEnvioMorada}</p>
                    <p>{d.moradaEnvioCodigoPostal} {d.moradaEnvioLocalidade}</p>
                    <p>{d.moradaEnvioPais}</p>
                  </div>
                ) : (
                  <InfoValue className="text-[#6B7280] italic">Encomenda sem morada de envio</InfoValue>
                )}
              </div>
              <div>
                <InfoLabel>Dados de Faturação</InfoLabel>
                {d.moradaFaturacaoMorada ? (
                  <div className="text-sm text-center text-[#111827] space-y-0.5">
                    <p>{d.moradaFaturacaoNome}</p>
                    <p>{d.moradaFaturacaoNif}</p>
                    <p>{d.moradaFaturacaoMorada}</p>
                    <p>{d.moradaFaturacaoCodigoPostal} {d.moradaFaturacaoLocalidade}</p>
                    <p>{d.moradaFaturacaoPais}</p>
                  </div>
                ) : (
                  <InfoValue className="text-[#6B7280] italic">Encomenda sem morada de faturação</InfoValue>
                )}
              </div>
            </div>

            {/* Row 5: Descontos */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <InfoLabel>Descontos de Crédito</InfoLabel>
                <InfoValue className="font-mono">{fmt(d.DescontoCredito, cur)}</InfoValue>
              </div>
              <div>
                <InfoLabel>Desconto Total</InfoLabel>
                <InfoValue className="font-mono">{fmt(d.DescontoTotal, cur)}</InfoValue>
              </div>
            </div>

            {/* Row 6: IVA + Transporte */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <InfoLabel>Iva Total</InfoLabel>
                <InfoValue className="font-mono">{fmt(d.IvaTotal, cur)}</InfoValue>
              </div>
              <div>
                <InfoLabel>Custo de Transporte</InfoLabel>
                <InfoValue className="font-mono">{fmt(d.CustoTransporte, cur)}</InfoValue>
              </div>
            </div>

            {/* Row 7: NetPoints + Preços */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <InfoLabel>NetPoints</InfoLabel>
                <InfoValue className="font-mono">{d.NetPointsPessoal ? Number(d.NetPointsPessoal).toFixed(2) : '0.00'}</InfoValue>
              </div>
              <div>
                <InfoLabel>Preço Total (Produtos s/IVA)</InfoLabel>
                <InfoValue className="font-mono">{fmt(d.PrecoTotal < 0 ? 0 : d.PrecoTotal, cur)}</InfoValue>
              </div>
              <div>
                <InfoLabel>Preço Final (Total + IVA + Portes - Descontos)</InfoLabel>
                <InfoValue className="font-mono font-semibold text-[#0F1829]">{fmt(d.PrecoFinal, cur)}</InfoValue>
              </div>
            </div>

            {/* Row 8: Observações */}
            <div>
              <InfoLabel>Observações</InfoLabel>
              <InfoValue><HtmlContent html={d.Observacao} className="text-[#6B7280]" /></InfoValue>
            </div>
          </div>
        )}
      </div>

      {/* SECÇÃO 2: Itens da Encomenda */}
      <div data-testid="order-items-section" className="bg-white border border-[#E5E7EB] rounded-lg mb-4 overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
          <Package size={18} className="text-[#6B7280]" />
          <h2 className="text-sm font-semibold text-[#111827]">Itens da encomenda</h2>
          <span className="text-xs text-[#6B7280] ml-1">({items.length})</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-2.5">Nome</th>
                <th className="text-center text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Taxa IVA</th>
                <th className="text-center text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Quantidade</th>
                <th className="text-center text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">NetPoints</th>
                <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Créditos</th>
                <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Preço Unit. (C/ IVA)</th>
                <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Preço Total (C/ IVA)</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={`item-${item.ID_ItemEncomenda || item.Descricao || i}-${i}`} data-testid={`order-item-row-${i}`} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] transition-colors">
                  <td className="px-4 py-2.5 text-sm text-[#111827]">
                    {(() => {
                      if (item.ID_Produto) return <span className="text-[#6B7280] mr-1">({item.ID_Produto})</span>;
                      if (item.ID_Pack) return <span className="text-[#6B7280] mr-1">(pack {item.ID_Pack})</span>;
                      return null;
                    })()}
                    {item.Descricao || '-'}
                  </td>
                  <td className="px-3 py-2.5 text-sm text-center text-[#6B7280]">{item.TaxaIva != null ? fmtPct(item.TaxaIva) : '-'}</td>
                  <td className="px-3 py-2.5 text-sm text-center text-[#6B7280]">{item.Quantidade || '-'}</td>
                  <td className="px-3 py-2.5 text-sm text-center font-mono text-[#6B7280]">{item.TotalNetPoints != null ? item.TotalNetPoints : '-'}</td>
                  <td className="px-3 py-2.5 text-sm text-right font-mono text-[#6B7280]">{item.TotalCreditos != null ? fmt(item.TotalCreditos, item.ID_Moeda || cur) : '-'}</td>
                  <td className="px-3 py-2.5 text-sm text-right font-mono text-[#6B7280]">{item.PrecoUnitarioComIva != null ? fmt(item.PrecoUnitarioComIva, item.ID_Moeda || cur) : '-'}</td>
                  <td className="px-3 py-2.5 text-sm text-right font-mono font-medium text-[#111827]">{item.Preco != null ? fmt(item.Preco, item.ID_Moeda || cur) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && <p className="text-center py-6 text-sm text-[#6B7280]">Sem itens da encomenda para apresentar</p>}
        </div>
      </div>

      {/* SECÇÃO 2.5: Documentos de faturação */}
      {billing && (billing.facturas?.length > 0 || billing.recibos?.length > 0 || billing.notas_credito?.length > 0) && (
        <div data-testid="order-billing-section" className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
            <Receipt size={18} className="text-[#6B7280]" />
            <h2 className="text-sm font-semibold text-[#111827]">Documentos de faturação</h2>
          </div>
          <ul className="divide-y divide-[#F3F4F6]">
            {(billing.facturas || []).map((f) => (
              <li key={`ft-${f.ID_Factura}`} className="flex items-center justify-between px-5 py-3 hover:bg-[#F9FAFB]">
                <div>
                  <span className="inline-block text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 rounded bg-blue-50 text-blue-900 border border-blue-200 mr-2">
                    {f.TipoFactura === 'F' ? 'Fatura' : (f.TipoFactura === 'FR' ? 'Fatura-Recibo' : 'Fatura')}
                  </span>
                  <span className="font-mono text-sm text-[#111827]">{f.NrFactura}</span>
                  <span className="text-xs text-[#6B7280] ml-3">{fmtDate(f.Data)}</span>
                </div>
                {f.UrlFatura ? (
                  <a href={f.UrlFatura} target="_blank" rel="noopener noreferrer"
                     data-testid={`link-fatura-${f.ID_Factura}`}
                     className="text-sm text-blue-700 hover:text-blue-900 underline">
                    Descarregar PDF
                  </a>
                ) : (
                  <span className="text-xs text-[#9CA3AF]">Sem link disponível</span>
                )}
              </li>
            ))}
            {(billing.recibos || []).map((r) => (
              <li key={`rb-${r.ID_Recibo}`} className="flex items-center justify-between px-5 py-3 hover:bg-[#F9FAFB]">
                <div>
                  <span className="inline-block text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 rounded bg-emerald-50 text-emerald-900 border border-emerald-200 mr-2">
                    Recibo
                  </span>
                  <span className="font-mono text-sm text-[#111827]">{r.NrRecibo}</span>
                  <span className="text-xs text-[#6B7280] ml-3">{fmtDate(r.Data)}</span>
                </div>
                {r.UrlRecibo ? (
                  <a href={r.UrlRecibo} target="_blank" rel="noopener noreferrer"
                     data-testid={`link-recibo-${r.ID_Recibo}`}
                     className="text-sm text-blue-700 hover:text-blue-900 underline">
                    Descarregar PDF
                  </a>
                ) : (
                  <span className="text-xs text-[#9CA3AF]">Sem link disponível</span>
                )}
              </li>
            ))}
            {(billing.notas_credito || []).map((n) => (
              <li key={`nc-${n.ID_NotaCredito}`} className="flex items-center justify-between px-5 py-3 hover:bg-[#F9FAFB]">
                <div>
                  <span className="inline-block text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 rounded bg-amber-50 text-amber-900 border border-amber-200 mr-2">
                    Nota de Crédito
                  </span>
                  <span className="font-mono text-sm text-[#111827]">{n.NrNotaCredito}</span>
                  <span className="text-xs text-[#6B7280] ml-3">{fmtDate(n.Data)}</span>
                </div>
                {n.UrlNotaCredito ? (
                  <a href={n.UrlNotaCredito} target="_blank" rel="noopener noreferrer"
                     data-testid={`link-nota-${n.ID_NotaCredito}`}
                     className="text-sm text-blue-700 hover:text-blue-900 underline">
                    Descarregar PDF
                  </a>
                ) : (
                  <span className="text-xs text-[#9CA3AF]">Sem link disponível</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* SECÇÃO 3: Evolução da Encomenda (Estados) */}
      <div data-testid="order-states-section" className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
          <ChartBar size={18} className="text-[#6B7280]" />
          <h2 className="text-sm font-semibold text-[#111827]">Evolução da encomenda</h2>
          <span className="text-xs text-[#6B7280] ml-1">({states.length})</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-2.5">Estado de Encomenda</th>
                <th className="text-center text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Data</th>
                <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Observação</th>
                <th className="text-center text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Email Enviado</th>
              </tr>
            </thead>
            <tbody>
              {states.map((s, i) => (
                <tr key={`state-${s.EstadoEncomenda || 's'}-${s.data || i}`} data-testid={`order-state-row-${i}`} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] transition-colors">
                  <td className="px-4 py-2.5">
                    <span className="text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 rounded border bg-blue-50 text-[#0F1829] border-[#0F1829]/20">
                      {s.EstadoEncomenda || '-'}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-sm text-center text-[#6B7280]">{fmtDate(s.data)}</td>
                  <td className="px-3 py-2.5 text-sm text-[#6B7280]">{s.observacao || 'Não existem observações'}</td>
                  <td className="px-3 py-2.5 text-sm text-center text-[#6B7280]">{s.enviado || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {states.length === 0 && <p className="text-center py-6 text-sm text-[#6B7280]">Sem estados da encomenda para apresentar</p>}
        </div>
      </div>
    </div>
  );
}
